import React, { useState, useEffect, useRef, TouchEvent, MouseEvent } from 'react';
import { BoxComponent } from '../../base/box/Box';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import { carouselStyles } from './carousel.styles';
import { CarouselProps, ICON_PLACEMENT } from './carousel.types';
import { hexToRgba } from '../../sections/Commons';
import Image from '../../sections/SharedComponents/Image';

const Carousel: React.FC<CarouselProps> = ({
  images,
  style,
  interval = 2000,
  isBuilderDesktopMode,
  iconPlacement,
  iconColor = '#ffffff',
  autoScroll = true,
  returnIndex,
  MediaMap,
  overlayStyles,
  seoTitle,
  initialIndex = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isDragging = useRef(false);
  const startTime = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (returnIndex) {
      returnIndex(currentIndex);
    }
  }, [currentIndex]);

  // Sync internal state with initialIndex prop only when images change or component remounts
  // This prevents resetting when parent re-renders due to text editing
  const prevImagesLengthRef = useRef(images.length);
  useEffect(() => {
    // Only sync if images array length changed (new images) or if current index is out of bounds
    if (images.length !== prevImagesLengthRef.current) {
      prevImagesLengthRef.current = images.length;
      if (initialIndex !== undefined) {
        setCurrentIndex(Math.max(0, Math.min(initialIndex, images.length - 1)));
      }
    } else if (currentIndex >= images.length && images.length > 0) {
      // If current index is out of bounds, reset to valid index
      setCurrentIndex(Math.max(0, Math.min(initialIndex || 0, images.length - 1)));
    }
  }, [images.length, initialIndex]);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      goToNext();
    }, interval);
  };

  useEffect(() => {
    // Only reset to initialIndex if images array actually changed (not just re-render)
    // Preserve current index if it's still valid
    if (currentIndex >= images.length) {
      setCurrentIndex(Math.max(0, Math.min(initialIndex, images.length - 1)));
    }
    if (images.length <= 1) return;
    if (autoScroll) {
      startTimer();
    } else {
      // Make sure to clear any existing timer if autoScroll is false
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [images.length, interval, autoScroll, initialIndex]);

  const goToPrevious = () => {
    setSlideDirection('right');
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
    if (autoScroll) startTimer();
  };

  const goToNext = () => {
    setSlideDirection('left');
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    if (autoScroll) startTimer();
  };

  const handleDragStart = (clientX: number) => {
    touchStartX.current = clientX;
    isDragging.current = false;
    startTime.current = Date.now();
  };

  const handleDragMove = (clientX: number) => {
    touchEndX.current = clientX;
    if (Math.abs(touchEndX.current - touchStartX.current) > 10) {
      isDragging.current = true;
    }
  };

  const handleDragEnd = () => {
    const dragDistance = touchStartX.current - touchEndX.current;
    const dragDuration = Date.now() - startTime.current;

    if (isDragging.current && Math.abs(dragDistance) > 75 && dragDuration < 500) {
      if (dragDistance > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
    isDragging.current = false;
  };

  const goToSlide = (index: number) => {
    if (index === currentIndex) return;
    setSlideDirection(index > currentIndex ? 'left' : 'right');
    setCurrentIndex(index);
    if (autoScroll) startTimer();
  };

  const handleTouchStart = (e: TouchEvent) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleMouseDown = (e: MouseEvent) => {
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (e.buttons === 1) {
      handleDragMove(e.clientX);
    }
  };

  const prevIndexRef = useRef(currentIndex);
  useEffect(() => {
    prevIndexRef.current = currentIndex;
  }, [currentIndex]);

  if (images.length === 0) return null;

  const Navigation = () => (
    <>
      <IconButton
        sx={isBuilderDesktopMode ? carouselStyles.navigationIcon : carouselStyles.navigationIcon_Mobile}
        aria-label="Previous image"
        onClick={goToPrevious}
      >
        <ChevronLeftIcon sx={{ fontSize: 40, color: iconColor }} />
      </IconButton>
      <IconButton
        sx={
          isBuilderDesktopMode
            ? { ...carouselStyles.navigationIcon, right: '10px', left: 'auto' }
            : carouselStyles.navigationIcon_Mobile
        }
        aria-label="Next image"
        onClick={goToNext}
      >
        <ChevronRightIcon sx={{ fontSize: 40, color: iconColor }} />
      </IconButton>
      <BoxComponent
        sx={{
          ...carouselStyles.bottomNavigator,
          bottom: iconPlacement === ICON_PLACEMENT.ON_IMAGE ? '20px' : '-40px',
        }}
      >
        {images.map((_, index) => (
          <BoxComponent
            key={index}
            sx={{
              ...carouselStyles.dot,
              ...(index === currentIndex
                ? {
                    backgroundColor: hexToRgba(iconColor, 0.9),
                    border: `2px solid ${hexToRgba(iconColor, 0.1)}`,
                    transform: 'scale(1.2)',
                  }
                : {
                    backgroundColor: hexToRgba(iconColor, 0.5),
                  }),
            }}
            onClick={() => goToSlide(index)}
          />
        ))}
      </BoxComponent>
    </>
  );

  const calculateOffset = (index: number, curIndex: number) => {
    const n = images.length;
    let diff = (index - curIndex) % n;
    if (diff > n / 2) diff -= n;
    if (diff <= -n / 2) diff += n;
    return diff;
  };

  return (
    <BoxComponent sx={{ position: 'unset', display: 'flex', justifyContent: 'center', width: '100%' }}>
      <BoxComponent
        sx={{ ...carouselStyles.container, ...style }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleDragEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
      >
        {images.map((image, index) => {
          const offset = calculateOffset(index, currentIndex);
          const prevOffset = calculateOffset(index, prevIndexRef.current);
          const isJumpingAcross = Math.abs(offset - prevOffset) > 1.1;

          return (
            <BoxComponent
              key={index}
              sx={{
                ...carouselStyles.slide,
                transform: `translateX(${offset * 100}%)`,
                transition: slideDirection && !isJumpingAcross ? 'transform 0.3s ease-in-out' : 'none',
                visibility: Math.abs(offset) > 1.5 ? 'hidden' : 'visible',
                // Apply overlay only to slides, not buttons
                '::after': overlayStyles
                  ? {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: hexToRgba(
                        overlayStyles?.overlayColor || '#000000',
                        overlayStyles?.overlayOpacity || 0
                      ),
                      pointerEvents: 'none',
                      zIndex: 1, // Above image but below buttons (zIndex: 10)
                      backdropFilter: `blur(${overlayStyles?.blurValue || 0}px)`,
                      WebkitBackdropFilter: `blur(${overlayStyles?.blurValue || 0}px)`,
                    }
                  : undefined,
              }}
            >
              <Image
                key={index}
                imageUrl={image.croppedFileUrl || image.fileUrl}
                MediaMap={MediaMap}
                lazyLoad={index === 0 ? false : true}
                alt={seoTitle || image.name}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  zIndex: 0,
                }}
              />
            </BoxComponent>
          );
        })}
        {iconPlacement === ICON_PLACEMENT.ON_IMAGE && <Navigation />}
      </BoxComponent>
      {iconPlacement === ICON_PLACEMENT.OUTSIDE_IMAGE && <Navigation />}
    </BoxComponent>
  );
};

export default Carousel;
